import { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Scrolls to the top of the page instantly on every route change.
 *
 * The site has `scroll-behavior: smooth` globally in App.css, so we must
 * explicitly pass `behavior: 'auto'` to force an instant jump.
 *
 * We scroll twice — once synchronously before paint (useLayoutEffect)
 * and once on the next animation frame — to catch any post-paint
 * layout shifts that would otherwise leave the user partway down the page.
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  useLayoutEffect(() => {
    const jumpToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    };

    // 1. Before first paint (useLayoutEffect is sync)
    jumpToTop();

    // 2. After first paint (catches any post-render layout shifts)
    const rafId = requestAnimationFrame(jumpToTop);

    return () => cancelAnimationFrame(rafId);
  }, [pathname]);

  return null;
};

export default ScrollToTop;
